import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { errorMessage, registerPasskey } from '../../lib/passkey.js';
import { Alert, Spinner, PasswordField } from '../../lib/ui.js';
import { t } from '../../i18n/index.js';

type Info = {
  state: 'valid' | 'used' | 'expired' | 'revoked' | 'missing';
  name?: string;
  email?: string;
};

/** Admin passkey enrolment via link + password. */
export function EnrolPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState<Info | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<Info>(`/auth/enrol/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setInfo({ state: 'missing' }));
  }, [token]);

  if (!info) return <Spinner />;
  if (info.state !== 'valid') {
    return (
      <div className="auth-form">
        <Alert>{t(`invite_${info.state}`)}</Alert>
      </div>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const path = `/auth/enrol/${encodeURIComponent(token)}`;
      await registerPasskey(`${path}/options`, `${path}/verify`, { password });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-form">
      <h2>{t('enrol_title')}</h2>
      <p>{t('enrol_intro', { name: info.name ?? '' })}</p>
      <form onSubmit={submit}>
        <label>
          {t('email')}
          <input type="email" value={info.email ?? ''} readOnly />
        </label>
        <PasswordField
          id="enrol-password"
          label={t('password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        {error && <Alert>{error}</Alert>}
        <button type="submit" className="big" disabled={busy}>
          {busy ? t('passkey_setting_up') : t('passkey_set_up')}
        </button>
      </form>
    </div>
  );
}
